import React, { useEffect, useRef, useState } from "react";
import { api, downloadPost, type Me } from "./api";
import { eastern } from "./RecordScreen";

/**
 * The print center (build plan §9.1 #10; Decisions 2 #27): the paper
 * fallback. A student's packet (PDF: profile, allergies, confirmed
 * medications, OTC permissions and a week of MAR sheets), the day's
 * med-pass sheets and gathering lists per window and city (print-styled
 * pages for the browser to print), and "Print everything for tomorrow".
 * Every print is an export (System Admin, Supervisor, RN), audited.
 */

interface WindowDef { code: string; name: string; startTime: string; endTime: string }
interface Options { windows: WindowDef[]; cities: string[]; today: string; tomorrow: string }

interface SheetRow {
  personKey: string;
  lastName: string;
  firstName: string;
  dateOfBirth: string;
  city: string;
  allergies: string;
  medication: string;
  dose: string;
  route: string;
  ordered: string;
  prn: boolean;
  controlled: boolean;
  recorded: string;
}
interface PassSheet { date: string; window: WindowDef; city: string; rows: SheetRow[] }
interface GatheringList { date: string; window: WindowDef; city: string; people: { personKey: string; lastName: string; firstName: string; city: string; doses: number; ask: boolean }[] }
interface Sheets { date: string; passSheets: PassSheet[]; gatheringLists: GatheringList[] }

const cityName = (c: string) => (c === "" ? "All cities" : c === "(not assigned)" ? "Not assigned" : c);
const longDay = (d: string) => new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "long", month: "long", day: "numeric", year: "numeric" }).format(new Date(`${d}T12:00:00Z`));

function PacketPicker() {
  const [q, setQ] = useState("");
  const [people, setPeople] = useState<{ personKey: string; lastName: string; firstName: string; city: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (!term) return setPeople([]);
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      const r = await api<{ people: typeof people }>("GET", `/api/station/search?q=${encodeURIComponent(term)}`);
      if (mine === seq.current && r.ok) setPeople(r.data.people);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="section-card">
      <h3 className="mb-1 text-base font-semibold">A student's packet (PDF)</h3>
      <p className="mb-2 text-sm text-slate-600">Profile, allergies and the allergy response, confirmed medications, OTC permissions, and a week of MAR sheets (one per window, a row per dose) with an as-needed log.</p>
      <label htmlFor="pc-q" className="field-label">Find the student</label>
      <input id="pc-q" className="field-input" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
      {error && <p className="field-error" role="alert">{error}</p>}
      <ul className="mt-2 divide-y divide-slate-100">
        {people.map((p) => (
          <li key={p.personKey} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm">
            <span>{p.lastName}, {p.firstName} <span className="text-slate-500">· {p.personKey} · {p.city || "Not assigned"}</span></span>
            <button type="button" className="btn-secondary" disabled={busy === p.personKey} onClick={async () => { setBusy(p.personKey); setError(await downloadPost(`/api/print/packet/${encodeURIComponent(p.personKey)}`, "student-packet.pdf")); setBusy(null); }}>
              {busy === p.personKey ? "Preparing…" : "Download packet"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SheetPicker({ o }: { o: Options }) {
  const [win, setWin] = useState(o.windows[0]?.code ?? "");
  const [city, setCity] = useState(o.cities[0] ?? "");
  const [date, setDate] = useState(o.today);
  const q = (kind: string) => `/print/sheets?${new URLSearchParams({ kind, window: win, city, date })}`;
  return (
    <div className="section-card">
      <h3 className="mb-1 text-base font-semibold">Med-pass sheet and gathering list</h3>
      <p className="mb-2 text-sm text-slate-600">For one window and city: the pass's rows with blank columns to fill in, and the list of who to send to the station.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div><label htmlFor="ps-w" className="field-label">Window</label><select id="ps-w" className="field-input" value={win} onChange={(e) => setWin(e.target.value)}>{o.windows.map((w) => <option key={w.code} value={w.code}>{w.name} ({w.startTime}–{w.endTime})</option>)}</select></div>
        <div><label htmlFor="ps-c" className="field-label">City</label><select id="ps-c" className="field-input" value={city} onChange={(e) => setCity(e.target.value)}>{o.cities.map((c) => <option key={c} value={c}>{c}</option>)}<option value="(not assigned)">Not assigned</option><option value="">All cities</option></select></div>
        <div><label htmlFor="ps-d" className="field-label">Day</label><input id="ps-d" type="date" className="field-input" value={date} onChange={(e) => setDate(e.target.value)} /></div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <a className="btn-secondary" href={q("pass")}>Med-pass sheet</a>
        <a className="btn-secondary" href={q("gathering")}>Gathering list</a>
      </div>
    </div>
  );
}

/** After the session (M5): the return sheet per city, and the end-of-week reports to print. */
function AfterSession({ o }: { o: Options }) {
  const [city, setCity] = useState("");
  return (
    <div className="section-card space-y-2">
      <h3 className="text-base font-semibold">After the session</h3>
      <p className="text-sm text-slate-600">The medication return sheet: one page per city, a row per medication the med station still holds (not the controlled ones: those are returned at the med station), with signature lines for the student or parent and the counselor.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div><label htmlFor="pc-rc" className="field-label">City</label><select id="pc-rc" className="field-input" value={city} onChange={(e) => setCity(e.target.value)}><option value="">Every city</option>{o.cities.map((c) => <option key={c} value={c}>{c}</option>)}<option value="(not assigned)">Not assigned</option></select></div>
        <a className="btn-primary inline-flex min-h-11 items-center" href={`/api/print/return-sheet${city ? `?city=${encodeURIComponent(city)}` : ""}`}>Print the return sheet</a>
      </div>
      <p className="text-sm text-slate-600">The parent end-of-week reports (#21): every student's in one PDF. One student's is on <a className="text-program underline" href="/eow">#21</a>.</p>
      <button type="button" className="btn-secondary min-h-11" onClick={() => void downloadPost("/api/print/eow", "end-of-week-reports.pdf")}>Print all end-of-week reports</button>
    </div>
  );
}

function CenterIndex() {
  const [o, setO] = useState<Options | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api<Options>("GET", "/api/print/options").then((r) => (r.ok ? setO(r.data) : setError(r.data.error ?? "Could not load.")));
  }, []);
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/pass" className="text-program underline">← Medication pass</a></p>
      <h2 className="mb-1 text-xl font-semibold">Print center</h2>
      <p className="mb-3 text-sm text-slate-600">The paper fallback. Every print is recorded. Keep printed pages with you, and shred them when they are no longer needed.</p>
      {error && <p className="field-error" role="alert">{error}</p>}
      {o && (
        <>
          <div className="section-card">
            <h3 className="mb-1 text-base font-semibold">Print everything for tomorrow</h3>
            <p className="mb-2 text-sm text-slate-600">{longDay(o.tomorrow)}: every window's med-pass sheet and gathering list, for every city with a dose due.</p>
            <a className="btn-primary" href="/print/sheets?tomorrow=1">Print everything for tomorrow</a>
          </div>
          <SheetPicker o={o} />
          <PacketPicker />
          <AfterSession o={o} />
        </>
      )}
    </section>
  );
}

/** The med-pass sheet (Report #11's rows): blank Given / Amount / Time / Initials. */
export function PassSheetView({ s }: { s: PassSheet }): React.ReactElement {
  return (
    <div className="break-after-page">
      <h3 className="text-lg font-semibold">Med-pass sheet · {s.window.name} ({s.window.startTime}–{s.window.endTime}) · {cityName(s.city)} · {longDay(s.date)}</h3>
      <p className="mb-2 text-xs text-slate-700">Program standard (MAR): for each dose mark Given Y/N (or held/refused and why), the amount, the time and your initials; enter it in the app later with "Entering from paper".</p>
      {s.rows.length === 0 ? <p className="text-sm">No doses due.</p> : (
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr className="border-b-2 border-slate-700">
              {["Student", "Born", "Allergies", "Medication", "Dose", "Route", "As ordered", "Given", "Amount", "Time", "Initials"].map((h) => <th key={h} className="border border-slate-400 px-1 py-1">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r, i) => (
              <tr key={i} className="break-inside-avoid align-top">
                <td className="border border-slate-400 px-1 py-2 font-medium">{r.lastName}, {r.firstName}</td>
                <td className="border border-slate-400 px-1 py-2">{r.dateOfBirth}</td>
                <td className={`border border-slate-400 px-1 py-2 ${r.allergies !== "None" ? "font-semibold" : ""}`}>{r.allergies}</td>
                <td className="border border-slate-400 px-1 py-2">{r.medication}{r.controlled ? " (controlled: count)" : ""}</td>
                <td className="border border-slate-400 px-1 py-2">{r.dose}</td>
                <td className="border border-slate-400 px-1 py-2">{r.route}</td>
                <td className="border border-slate-400 px-1 py-2">{r.prn ? `AS NEEDED — ask. ${r.ordered}` : r.ordered}</td>
                <td className="w-12 border border-slate-400 px-1 py-2">{r.recorded}</td>
                <td className="w-14 border border-slate-400 px-1 py-2"></td>
                <td className="w-12 border border-slate-400 px-1 py-2"></td>
                <td className="w-12 border border-slate-400 px-1 py-2"></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** The gathering list (Report #10): who to send to the station; no medication names. */
export function GatheringView({ g }: { g: GatheringList }): React.ReactElement {
  return (
    <div className="break-after-page">
      <h3 className="text-lg font-semibold">Gathering list · {g.window.name} ({g.window.startTime}–{g.window.endTime}) · {cityName(g.city)} · {longDay(g.date)}</h3>
      <p className="mb-2 text-xs text-slate-700">Send these students to the med station during the window.</p>
      {g.people.length === 0 ? <p className="text-sm">No one.</p> : (
        <table className="w-full border-collapse text-left text-sm">
          <thead><tr>{["", "Student", "City", "Doses", "Sent"].map((h) => <th key={h} className="border border-slate-400 px-2 py-1">{h}</th>)}</tr></thead>
          <tbody>
            {g.people.map((p, i) => (
              <tr key={p.personKey} className="break-inside-avoid">
                <td className="w-8 border border-slate-400 px-2 py-2 text-right">{i + 1}</td>
                <td className="border border-slate-400 px-2 py-2 font-medium">{p.lastName}, {p.firstName}</td>
                <td className="border border-slate-400 px-2 py-2">{p.city || "Not assigned"}</td>
                <td className="border border-slate-400 px-2 py-2">{p.ask ? "As needed: ask" : p.doses}</td>
                <td className="w-16 border border-slate-400 px-2 py-2"></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SheetsView({ me }: { me: Me }) {
  const [d, setD] = useState<{ sheets: Sheets; generatedAtUtc: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const search = window.location.search;
  useEffect(() => {
    void api<{ sheets: Sheets; generatedAtUtc: string }>("GET", `/api/print/sheets${search}`).then((r) => (r.ok ? setD(r.data) : setError(r.data.error ?? "Could not load.")));
  }, [search]);
  return (
    <section>
      <style>{"@media print { @page { size: letter landscape; margin: 0.4in; } }"}</style>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 print:hidden">
        <a href="/print" className="text-sm text-program underline">← Print center</a>
        {d && <button type="button" className="btn-primary" onClick={() => window.print()}>Print</button>}
      </div>
      {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
      {!d && !error && <p className="text-sm text-slate-600">Loading…</p>}
      {d && (
        <div className="bg-white p-2 print:p-0">
          <p className="mb-3 text-xs text-slate-600">Printed {eastern(d.generatedAtUtc)} by {me.name}. Confidential health information: keep it with you, and shred it when it is no longer needed.</p>
          {d.sheets.gatheringLists.length + d.sheets.passSheets.length === 0 && <p className="text-sm">Nothing to print: no doses due.</p>}
          {d.sheets.gatheringLists.map((g) => <GatheringView key={`g-${g.window.code}-${g.city}`} g={g} />)}
          {d.sheets.passSheets.map((s) => <PassSheetView key={`p-${s.window.code}-${s.city}`} s={s} />)}
        </div>
      )}
    </section>
  );
}

export function PrintCenter({ path, me }: { path: string; me: Me }): React.ReactElement {
  return path === "/print/sheets" ? <SheetsView me={me} /> : <CenterIndex />;
}
